import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { useAnalysisCuboidActions } from '@/components/Analysis/modules/cuboids/hooks';
import * as state from '@/components/Analysis/state';
import { abyssColors } from '@/theme/colors';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import { useAtomValue } from 'jotai';
import { MouseEvent, useMemo, useState } from 'react';
import { useTriggerPoiEditing } from '../../hooks';
import { useRemeasureProfile } from '../../hooks/useRemeasureProfile';
import { PointOfInterest, PointOfInterestTemplate } from '@/__generated__/graphql';
import { PoiFormState } from '@/types';

type DetailsOptionMenuProps = {
  selectedPoiInfo: PointOfInterest;
  formState?: PoiFormState;
};

export const DetailsOptionMenu = ({
  selectedPoiInfo,
  formState = 'Update-Template',
}: DetailsOptionMenuProps) => {
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorElement);

  const { triggerEdit } = useTriggerPoiEditing();
  const { startEditingPoiCuboids } = useAnalysisCuboidActions();
  const { remeasureProfile } = useRemeasureProfile(selectedPoiInfo);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  const handleInitPOI = () => {
    remeasureProfile();
    handleClose();
  };

  const templateOfSelectedPoi = useMemo(() => {
    return allPointOfInterestTemplates.find(({ id }) => id === selectedPoiInfo.template?.id);
  }, [allPointOfInterestTemplates, selectedPoiInfo]);

  const handleEdit = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (templateOfSelectedPoi) {
      triggerEdit({
        formState,
        pointOfInterest: selectedPoiInfo,
        template: templateOfSelectedPoi as PointOfInterestTemplate,
      });
      startEditingPoiCuboids(selectedPoiInfo);
    }
    handleClose();
  };

  return (
    <Box>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        disableRipple
        sx={{ padding: 0 }}
      >
        <MoreVertIcon
          sx={{
            margin: 0,
            fontSize: '2.4rem',
            color: abyssColors.primary[500],
          }}
        />
      </IconButton>
      <Menu
        anchorEl={anchorElement}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '.MuiMenuItem-root': {
            minWidth: '15rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.8rem',
          },
        }}
      >
        <MenuItem onClick={handleEdit}>
          <AbyssIcon size="sm" iconName="measure" />
          <span>Edit</span>
        </MenuItem>

        {selectedPoiInfo.isAutoGenerated && (
          <MenuItem onClick={handleInitPOI}>
            <AbyssIcon size="sm" iconName="measure" />
            <span>Remeasure profile</span>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
